import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../../api/client';
import LeftSection from './component/LeftSection';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ScheduleIcon from '@mui/icons-material/Schedule';
import useExpiryWarning from '../../../ExpiryWarning/useExpiryWarning';
// import ExpiryWarningModal from '../../../ExpiryWarning/ExpiryWarningModal';
import ExpirySection from './component/ExpirySection';
import CreditExpiryModal from './component/CreditExpiryModal';
import TotalCreditsInfoModal from './component/TotalCreditsInfoModal';
import CurrentBalanceInfoModal from './component/CurrentBalanceInfoModal';
import InfoIcon from '@mui/icons-material/Info';
import LanguageWorldMap from './component/LanguageWorldMap';
import PageHeader, { PageCard, PageHeaderSelect, PageShell } from '../../../common/PageHeader';
import PieChartIcon from '@mui/icons-material/PieChart';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  CartesianGrid
} from 'recharts';

const THEME_GRADIENT = 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)';

const STAT_THEMES = {
  fuchsia: {
    bar: 'from-fuchsia-400 to-purple-500',
    wash: 'bg-[linear-gradient(165deg,#fdf4ff_0%,#ffffff_52%)]',
    icon: 'bg-fuchsia-50 text-fuchsia-600 ring-1 ring-fuchsia-100',
    glow: 'bg-[radial-gradient(120%_80%_at_100%_0%,rgba(217,70,239,0.16),transparent_58%)]',
    hover: 'hover:border-fuchsia-300 hover:shadow-[0_18px_36px_-16px_rgba(217,70,239,0.45)]',
  },
  sky: {
    bar: 'from-sky-400 to-blue-500',
    wash: 'bg-[linear-gradient(165deg,#f0f9ff_0%,#ffffff_52%)]',
    icon: 'bg-sky-50 text-sky-600 ring-1 ring-sky-100',
    glow: 'bg-[radial-gradient(120%_80%_at_100%_0%,rgba(14,165,233,0.18),transparent_58%)]',
    hover: 'hover:border-sky-300 hover:shadow-[0_18px_36px_-16px_rgba(14,165,233,0.45)]',
  },
  amber: {
    bar: 'from-amber-400 to-orange-500',
    wash: 'bg-[linear-gradient(165deg,#fffbeb_0%,#ffffff_52%)]',
    icon: 'bg-amber-50 text-amber-700 ring-1 ring-amber-100',
    glow: 'bg-[radial-gradient(120%_80%_at_100%_0%,rgba(245,158,11,0.2),transparent_58%)]',
    hover: 'hover:border-amber-300 hover:shadow-[0_18px_36px_-16px_rgba(245,158,11,0.42)]',
  },
  emerald: {
    bar: 'from-emerald-400 to-teal-500',
    wash: 'bg-[linear-gradient(165deg,#ecfdf5_0%,#ffffff_52%)]',
    icon: 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100',
    glow: 'bg-[radial-gradient(120%_80%_at_100%_0%,rgba(16,185,129,0.16),transparent_58%)]',
    hover: 'hover:border-emerald-300 hover:shadow-[0_18px_36px_-16px_rgba(16,185,129,0.42)]',
  },
};

const INSIGHT_THEMES = {
  sky: { bar: 'from-sky-400 to-blue-500', value: 'text-sky-600', wash: 'bg-sky-50/80', border: 'border-sky-100 hover:border-sky-300' },
  fuchsia: { bar: 'from-fuchsia-400 to-purple-500', value: 'text-fuchsia-600', wash: 'bg-fuchsia-50/80', border: 'border-fuchsia-100 hover:border-fuchsia-300' },
  amber: { bar: 'from-amber-400 to-orange-400', value: 'text-amber-600', wash: 'bg-amber-50/80', border: 'border-amber-100 hover:border-amber-300' },
  emerald: { bar: 'from-emerald-400 to-teal-500', value: 'text-emerald-600', wash: 'bg-emerald-50/80', border: 'border-emerald-100 hover:border-emerald-300' },
  rose: { bar: 'from-rose-400 to-red-500', value: 'text-rose-600', wash: 'bg-rose-50/80', border: 'border-rose-100 hover:border-rose-300' },
};

function SegmentButton({ active, onClick, children, className = '' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-3.5 py-1.5 text-sm font-semibold transition-all duration-200 cursor-pointer ${
        active ? 'text-white shadow-sm' : 'text-slate-600 hover:bg-white hover:text-slate-900'
      } ${className}`}
      style={active ? { background: THEME_GRADIENT } : undefined}
    >
      {children}
    </button>
  );
}

export default function SuperAdminDashboard({ onLogout }) {
  const { t } = useTranslation();
  const [cardData, setCardData] = useState({
    remainingCredit: 0,
    usedCredits: 0,
    totalCredits: 0
  });
  const [selectedCompany, setSelectedCompany] = useState('');
  const [companyList, setCompanyList] = useState([]);
  const [interviewInsights, setInterviewInsights] = useState({
    total_scheduled: 0,
    completed: 0,
    pending: 0,
    in_progress: 0,
    expired: 0,
    expired_items: [],
    by_department: []
  });
  const [activeUsers, setActiveUsers] = useState(0);
  const [activeTab, setActiveTab] = useState('chart');
  const [deptTab, setDeptTab] = useState('pie');

  const [expiryData, setExpiryData] = useState({
    latest_expiry_date: null,
    nearest_expiry_date: null,
    nearest_expiry_amount: 0,
    total_active_expiring: 0,
    contributing_buckets: [],
    total_expiring_credits: 0,
    upcoming_expiries: [],
    expired_credits: [],
    current_time: null
  });
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [showCreditsInfoModal, setShowCreditsInfoModal] = useState(false);
  const [showBalanceInfoModal, setShowBalanceInfoModal] = useState(false);
  const [numericCardData, setNumericCardData] = useState({
    remainingCredit: 0,
    usedCredits: 0,
    totalCredits: 0
  });
  const [availableBalance, setAvailableBalance] = useState(0);
  const [activeNextExpiry, setActiveNextExpiry] = useState(null);
  const role = localStorage.getItem('role');
  const {
    refetchExpiryInfo
  } = useExpiryWarning();

  const fetchCurrentBalance = useCallback(async () => {
    try {
      const userRole = localStorage.getItem('role');

      if (userRole === 'Candidate') {
        const res = await api.get('observability/user-credits/');
        const data = res.data.results || res.data;
        const balance = data.current_balance ?? 0;

        setAvailableBalance(balance);
        setCardData({
          remainingCredit: `${balance} ${t('dashboard.credits')}`,
          usedCredits: `${data.used_credits || 0} ${t('dashboard.credits')}`,
          totalCredits: `${data.lifetime_credits ?? 0} ${t('dashboard.credits')}`
        });
        setNumericCardData({
          remainingCredit: balance,
          usedCredits: data.used_credits ?? 0,
          totalCredits: data.lifetime_credits ?? 0
        });
        if (data.company_name) {
          setCompanyList(prev => {
            if (prev.length === 1 && prev[0].id === (data.company_id || data.company) && prev[0].name === data.company_name) {
              return prev;
            }
            return [{ id: data.company_id || data.company, name: data.company_name }];
          });
        }
      } else {
        // Fetch observability data first to resolve mappedCompanies
        const obsRes = await api.get('observability/credits/');
        const data = obsRes.data.results || obsRes.data;

        // Map unique companies as objects
        const mappedCompanies = Array.from(
          new Map(
            data
              .filter(d => d.company && d.company_name)
              .map(d => [d.company, { id: d.company, name: d.company_name }])
          ).values()
        );
        setCompanyList(prev => {
          if (prev.length !== mappedCompanies.length) return mappedCompanies;
          const isSame = prev.every((p, i) => p.id === mappedCompanies[i].id && p.name === mappedCompanies[i].name);
          return isSame ? prev : mappedCompanies;
        });

        const selectedCompanyObj = mappedCompanies.find(c => c.name === selectedCompany);
        const params = {};
        if (selectedCompanyObj) {
          params.company_id = selectedCompanyObj.id;
        }

        // Fetch available balance and lifetime totals in parallel
        const [balanceRes, lifetimeRes] = await Promise.all([
          api.get('credits/balance/', { params }),
          userRole === 'Super Admin' ? Promise.resolve(null) : api.get('credits/lifetime/').catch(() => null)
        ]);

        const balanceVal = balanceRes.data?.current_balance ?? 0;
        const nextExpiry = balanceRes.data?.next_expiry ?? null;

        // Use lifetime API for B2B/Candidates; for Super Admin use observability totals
        const lifetimeData = lifetimeRes?.data;
        const lifetimeTotalAdded = lifetimeData?.lifetime_total_added;
        const lifetimeTotalUsed = lifetimeData?.lifetime_total_used;

        const filteredData =
          selectedCompany && selectedCompany !== t('dashboard.allCompanies')
            ? data.filter(d => d.company_name === selectedCompany)
            : data;

        const totals = filteredData.reduce(
          (acc, curr) => {
            acc.total_credits += curr.total_credits;
            acc.used_credits += curr.used_credits;
            acc.remaining_credits += curr.remaining_credits || 0;
            return acc;
          },
          { total_credits: 0, used_credits: 0, remaining_credits: 0 }
        );

        const totalCreditsValue = userRole === 'Super Admin' ? totals.total_credits : (lifetimeTotalAdded ?? totals.total_credits);
        const usedCreditsValue = userRole === 'Super Admin' ? totals.used_credits : (lifetimeTotalUsed ?? totals.used_credits);
        const balance = userRole === 'Super Admin'
          ? (selectedCompany && selectedCompany !== t('dashboard.allCompanies') ? balanceVal : totals.remaining_credits)
          : balanceVal;

        setAvailableBalance(balance);
        if (userRole !== 'Super Admin' || (selectedCompany && selectedCompany !== t('dashboard.allCompanies'))) {
          setActiveNextExpiry(nextExpiry);
        }

        setCardData({
          remainingCredit: `${balance} ${t('dashboard.credits')}`,
          usedCredits: `${usedCreditsValue} ${t('dashboard.credits')}`,
          totalCredits: `${totalCreditsValue} ${t('dashboard.credits')}`
        });
        setNumericCardData({
          remainingCredit: balance,
          usedCredits: usedCreditsValue,
          totalCredits: totalCreditsValue
        });
      }
    } catch {
      setAvailableBalance(0);
      setActiveNextExpiry(null);
      setCardData({
        remainingCredit: `0 ${t('dashboard.credits')}`,
        usedCredits: `0 ${t('dashboard.credits')}`,
        totalCredits: `0 ${t('dashboard.credits')}`
      });
      setNumericCardData({
        remainingCredit: 0,
        usedCredits: 0,
        totalCredits: 0
      });
      setCompanyList([]);
    }
  }, [selectedCompany, t]);

  const normalizeInterviewInsights = useCallback((raw) => {
    const deriveTotalScheduled = (data) =>
      (data.completed || 0) +
      (data.pending || 0) +
      (data.in_progress || 0) +
      (data.expired || 0);

    if (!raw) {
      return {
        total_scheduled: 0,
        completed: 0,
        pending: 0,
        in_progress: 0,
        expired: 0,
        expired_items: [],
        by_department: []
      };
    }
    if (!Array.isArray(raw)) {
      const normalized = {
        completed: raw.completed || 0,
        pending: raw.pending || 0,
        in_progress: raw.in_progress || 0,
        expired: raw.expired || 0,
        expired_items: Array.isArray(raw.expired_items) ? raw.expired_items : [],
        by_department: raw.by_department || []
      };
      return {
        total_scheduled: deriveTotalScheduled(normalized),
        ...normalized
      };
    }
    const merged = raw.reduce(
      (a, c) => ({
        completed: a.completed + (c.completed || 0),
        pending: a.pending + (c.pending || 0),
        in_progress: a.in_progress + (c.in_progress || 0),
        expired: a.expired + (c.expired || 0),
        expired_items: [
          ...a.expired_items,
          ...(Array.isArray(c.expired_items) ? c.expired_items : [])
        ]
      }),
      {
        completed: 0,
        pending: 0,
        in_progress: 0,
        expired: 0,
        expired_items: []
      }
    );
    merged.total_scheduled = deriveTotalScheduled(merged);
    merged.expired_items = merged.expired_items.slice(0, 200);
    merged.by_department =
      raw.find((x) => x.by_department?.length)?.by_department || raw[0]?.by_department || [];
    return merged;
  }, []);

  const fetchInterviewInsights = useCallback(async () => {
    try {
      const selectedCompanyObj = companyList.find(c => c.name === selectedCompany);
      const params = {};
      if (selectedCompanyObj) {
        params.company_id = selectedCompanyObj.id;
      }
      const res = await api.get('observability/interview-insights/', { params });
      const payload = res.data.results || res.data;
      const data = normalizeInterviewInsights(payload);
      setInterviewInsights({
        total_scheduled: data.total_scheduled || 0,
        completed: data.completed || 0,
        pending: data.pending || 0,
        in_progress: data.in_progress || 0,
        expired: data.expired || 0,
        expired_items: data.expired_items || [],
        by_department: data.by_department || []
      });
    } catch { }
  }, [selectedCompany, companyList, normalizeInterviewInsights]);

  const fetchActiveUsers = useCallback(async () => {
    try {
      const selectedCompanyObj = companyList.find(c => c.name === selectedCompany);
      const params = {};
      if (selectedCompanyObj) {
        params.company_id = selectedCompanyObj.id;
      }
      const res = await api.get('admin/candidates/', { params });
      setActiveUsers(res.data?.pagination?.total_count || 0);
    } catch { }
  }, [selectedCompany, companyList]);

  const fetchExpiryData = useCallback(async () => {
    try {
      const role = localStorage.getItem('role');
      const selectedCompanyObj = companyList.find(c => c.name === selectedCompany);
      const params = {};
      if (selectedCompanyObj) {
        params.company_id = selectedCompanyObj.id;
      }
      
      let balancePromise;
      if (role === 'Candidate') {
        balancePromise = api.get('observability/user-credits/');
      } else {
        balancePromise = api.get('credits/balance/', { params });
      }

      const [balanceRes, summaryRes] = await Promise.all([
        balancePromise,
        api.get('observability/expiry-summary/', { params })
      ]);

      const balanceData = balanceRes.data?.results || balanceRes.data || {};
      const summaryData = summaryRes.data || {};

      const isSuperAdminAllCompanies = role === 'Super Admin' && (!selectedCompany || selectedCompany === t('dashboard.allCompanies'));
      
      const nextExpiry = isSuperAdminAllCompanies
        ? (summaryData.upcoming_expiries?.[0]?.expiry_date || null)
        : (balanceData.next_expiry || null);
        
      const nextExpiryAmount = isSuperAdminAllCompanies
        ? (summaryData.upcoming_expiries?.[0]?.amount || 0)
        : (balanceData.next_expiry_amount ?? 0);

      let totalActiveExpiring = 0;
      if (role === 'Candidate') {
        totalActiveExpiring = balanceData.current_balance ?? 0;
      } else if (isSuperAdminAllCompanies) {
        totalActiveExpiring = summaryData.total_expiring_credits ?? 0;
      } else {
        const contributing = balanceData.contributing_buckets || [];
        totalActiveExpiring = contributing.reduce((sum, b) => sum + (b.remaining_balance || 0), 0);
      }

      setExpiryData({
        ...summaryData,
        nearest_expiry_date: nextExpiry,
        nearest_expiry_amount: nextExpiryAmount,
        total_active_expiring: totalActiveExpiring,
        contributing_buckets: balanceData.contributing_buckets || []
      });

      if (isSuperAdminAllCompanies) {
        setActiveNextExpiry(nextExpiry);
      }
    } catch { }
  }, [selectedCompany, companyList, t]);

  useEffect(() => {
    fetchCurrentBalance();
    fetchInterviewInsights();
    fetchExpiryData();
    if (refetchExpiryInfo) refetchExpiryInfo();
    if (role !== "Candidate") fetchActiveUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, role]);

  const handleCompanyChange = (event) => setSelectedCompany(event.target.value);

  const statsCards = [
    {
      label: t('dashboard.totalCredits'),
      value: cardData.totalCredits,
      icon: <PeopleAltIcon fontSize="small" />,
      tone: 'fuchsia'
    },
    {
      label: t('dashboard.currentBalance'),
      value: cardData.remainingCredit,
      icon: <CreditCardIcon fontSize="small" />,
      tone: 'sky'
    },
    {
      label: t('dashboard.latestExpiry'),
      value: activeNextExpiry
        ? new Date(activeNextExpiry).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })
        : t('dashboard.noExpiry'),
      icon: <ScheduleIcon fontSize="small" />,
      tone: 'amber'
    },
    ...(role !== "Candidate" ? [{
      label: role === "Company Admin"
        ? t('dashboard.activeCandidates')
        : t('dashboard.activeUsers'),
      value: activeUsers,
      icon: <PeopleAltIcon fontSize="small" />,
      tone: 'emerald'
    }] : [])
  ];

  return (
    <PageShell
      fill={false}
      style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #f0f9ff 48%, #fdf4ff 100%)',
        fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <PageCard overflow={false}>
          <PageHeader
            title={t('dashboard.overview')}
            subtitle={t('dashboard.subtitle')}
            actions={
              role !== "Candidate" ? (
                companyList.length === 1 ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-blue-100">
                      {t('dashboard.company')}:
                    </span>
                    <span className="text-sm font-bold text-white">
                      {companyList[0]?.name}
                    </span>
                  </div>
                ) : (
                  <div className="min-w-60">
                    <PageHeaderSelect
                      value={selectedCompany}
                      onChange={handleCompanyChange}
                    >
                      <option value="">{t('dashboard.allCompanies')}</option>
                      {companyList.map((company, index) => (
                        <option key={index} value={company.name}>{company.name}</option>
                      ))}
                    </PageHeaderSelect>
                  </div>
                )
              ) : null
            }
          />

        <div className="space-y-4 bg-slate-50/80 px-3 py-3 sm:px-4 sm:py-4">
        {/* Row 1 — Stats Cards */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          {statsCards.map((card) => {
            const theme = STAT_THEMES[card.tone] || STAT_THEMES.sky;
            return (
              <div
                key={card.label}
                className={`group relative cursor-pointer overflow-hidden rounded-2xl border border-slate-200/90 text-left shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-all duration-200 hover:-translate-y-1 ${theme.wash} ${theme.hover}`}
                onClick={() => {
                  if (card.label === t('dashboard.latestExpiry')) {
                    setActiveTab('expiry');
                  } else if (card.label === t('dashboard.totalCredits')) {
                    setShowCreditsInfoModal(true);
                  } else if (card.label === t('dashboard.currentBalance')) {
                    setShowBalanceInfoModal(true);
                  }
                }}
              >
                <div className={`h-[0.05rem] w-full bg-gradient-to-r ${theme.bar}`} />
                <div className={`pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100 ${theme.glow}`} />
                <div className="relative z-[1] flex items-start justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      {card.label}
                    </p>
                    <p className="mt-2 text-xl font-semibold leading-tight text-slate-900">
                      {card.value}
                    </p>
                  </div>
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-transform duration-200 group-hover:scale-105 ${theme.icon}`}>
                    {card.icon}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Row 2 — Quick Insights */}
        <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <div className="mb-3 flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-sky-50 to-fuchsia-50 ring-1 ring-sky-100">
              <ShowChartIcon sx={{ fontSize: 18, color: '#0ea5e9' }} />
            </div>
            <h3 className="text-sm font-semibold text-slate-900">
              {t('dashboard.interviewInsights')}
            </h3>
          </div>

          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
            {[
              { label: t('dashboard.totalScheduled'), value: interviewInsights.total_scheduled, tone: 'sky' },
              { label: t('dashboard.completed'), value: interviewInsights.completed, tone: 'fuchsia' },
              { label: t('dashboard.pending'), value: interviewInsights.pending, tone: 'amber' },
              { label: t('dashboard.inProgress'), value: interviewInsights.in_progress, tone: 'emerald' },
              { label: t('dashboard.expired'), value: interviewInsights.expired, tone: 'rose' },
            ].map((item) => {
              const theme = INSIGHT_THEMES[item.tone];
              return (
                <div
                  key={item.label}
                  className={`overflow-hidden rounded-xl border text-center transition-all duration-200 hover:-translate-y-0.5 ${theme.wash} ${theme.border}`}
                >
                  <div className={`h-[0.05rem] w-full bg-gradient-to-r ${theme.bar}`} />
                  <div className="px-2 py-3">
                    <div className={`text-xl font-semibold ${theme.value}`}>
                      {item.value}
                    </div>
                    <div className="mt-1 text-[11px] font-semibold text-slate-500">
                      {item.label}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Row 3 — Three Columns Responsive Grid */}
        <div className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div className="h-full min-h-[520px] md:col-span-2 lg:col-span-1">
            <LanguageWorldMap />
          </div>

          <div className="flex h-[520px] flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="h-[0.05rem] w-full shrink-0 bg-gradient-to-r from-sky-400 to-fuchsia-500" />
            <div className="border-b border-slate-100 p-4 sm:p-5">
              <div className="mb-4 flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600 ring-1 ring-sky-100">
                    <ShowChartIcon sx={{ fontSize: 20 }} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold leading-tight text-slate-900">
                      {t('dashboard.creditAnalytics')}
                    </h3>
                    <p className="mt-1 text-xs font-medium text-slate-500">
                      {t('dashboard.trackUsageExpiry')}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setShowExpiryModal(true)}
                  className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-sm transition-all hover:brightness-105"
                  style={{ background: THEME_GRADIENT }}
                >
                  <InfoIcon sx={{ fontSize: 14 }} />
                  {t('dashboard.expiryDetail')}
                </button>
              </div>

              <div className="inline-flex gap-1 rounded-xl bg-slate-100 p-1">
                <SegmentButton active={activeTab === 'chart'} onClick={() => setActiveTab('chart')}>
                  {t('dashboard.usageAnalytics')}
                </SegmentButton>
                <SegmentButton active={activeTab === 'expiry'} onClick={() => setActiveTab('expiry')}>
                  {t('dashboard.expiringAt')}
                </SegmentButton>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-hidden p-4 sm:p-5">
              {activeTab === 'chart' ? <LeftSection selectedCompany={selectedCompany} /> : <ExpirySection expiryData={expiryData} />}
            </div>
          </div>

          <div className="flex h-[520px] flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="h-[0.05rem] w-full shrink-0 bg-gradient-to-r from-fuchsia-400 to-sky-500" />
            <div className="p-4 sm:p-5">
              <h3 className="mb-3 text-base font-semibold text-slate-900">
                {t('dashboard.byDepartment')}
              </h3>
              <div className="inline-flex gap-1 rounded-xl bg-slate-100 p-1">
                <SegmentButton active={deptTab === 'bar'} onClick={() => setDeptTab('bar')}>
                  <ShowChartIcon sx={{ fontSize: 16 }} />
                  <span className="hidden sm:inline">{t('dashboard.usage')}</span>
                </SegmentButton>
                <SegmentButton active={deptTab === 'pie'} onClick={() => setDeptTab('pie')}>
                  <PieChartIcon sx={{ fontSize: 16 }} />
                  <span className="hidden sm:inline">{t('dashboard.pie')}</span>
                </SegmentButton>
              </div>
            </div>

            <div className="min-h-0 flex-1 px-3 pb-3">
              {deptTab === 'bar'
                ? <DepartmentBarChart data={interviewInsights.by_department} />
                : <DepartmentPieChart data={interviewInsights.by_department} />
              }
            </div>
          </div>
        </div>
        </div>
      </PageCard>

        {/* Modal rendered via Portal */}
        <CreditExpiryModal
          isOpen={showExpiryModal}
          onClose={() => setShowExpiryModal(false)}
          expiryData={expiryData}
        />

        <TotalCreditsInfoModal
          isOpen={showCreditsInfoModal}
          onClose={() => setShowCreditsInfoModal(false)}
          numericData={numericCardData}
        />

        <CurrentBalanceInfoModal
          isOpen={showBalanceInfoModal}
          onClose={() => setShowBalanceInfoModal(false)}
          numericData={numericCardData}
        />
    </PageShell>
  );
}

/* BAR CHART */
const DepartmentBarChart = ({ data }) => {
  const COLORS = [
    "#0ea5e9", "#d946ef", "#10b981", "#f59e0b", "#ef4444",
    "#8b5cf6", "#06b6d4", "#f97316", "#ec4899", "#6366f1",
    "#14b8a6", "#a855f7", "#84cc16", "#eab308", "#64748b"
  ];

  const formatted = data.map((d, i) => ({
    name: d.job_description__department__name || `Dept ${i + 1}`,
    value: d.count || 0
  }));

  const getColor = (i) => COLORS[i % COLORS.length];

  // Custom Tooltip to show full department name
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            borderRadius: 12,
            border: '1px solid rgba(229, 229, 229, 0.3)',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            padding: '12px 16px',
            fontFamily: "'Poppins', sans-serif"
          }}
        >
          <p style={{ margin: 0, fontWeight: '600', color: '#171717', marginBottom: '4px' }}>
            {data.name}
          </p>
          <p style={{ margin: 0, fontSize: '14px', color: '#525252' }}>
            {data.value} {data.value === 1 ? 'interview' : 'interviews'}
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom tick component to show full name on hover
  const CustomTick = ({ x, y, payload }) => {
    const name = payload.value || '';
    const truncatedName = name.length > 15 ? `${name.substring(0, 15)}...` : name;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="end"
          fill="#525252"
          fontSize="11"
          fontFamily="'Poppins', sans-serif"
          transform="rotate(-45)"
        >
          <title>{name}</title>
          {truncatedName}
        </text>
      </g>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={formatted}
        margin={{ top: 10, right: 10, left: 0, bottom: 60 }}
      >
        <CartesianGrid
          stroke="rgba(229, 229, 229, 0.5)"
          strokeDasharray="3 3"
        />

        <XAxis
          dataKey="name"
          tick={<CustomTick />}
          interval={0}
          height={60}
        />

        <YAxis
          tick={{ fontSize: 12, fill: "#525252", fontFamily: "'Poppins', sans-serif" }}
          axisLine={false}
        />

        <Tooltip content={<CustomTooltip />} />

        <Bar dataKey="value" radius={[8, 8, 0, 0]}>
          {formatted.map((_, i) => (
            <Cell key={i} fill={getColor(i)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};


/* PIE CHART */
const DepartmentPieChart = ({ data }) => {
  const { t } = useTranslation();
  const COLORS = [
    "#0ea5e9", "#d946ef", "#10b981", "#f59e0b",
    "#ef4444", "#8b5cf6", "#06b6d4", "#f97316",
    "#ec4899", "#6366f1", "#14b8a6", "#a855f7"
  ];

  const formatted = data.map((d, i) => ({
    name: d.job_description__department__name || `Dept ${i + 1}`,
    value: d.count || 0
  }));

  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    if (percent < 0.05) return null; // Hide labels for slices < 5%

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize="11"
        fontWeight="600"
        style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.7)' }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="relative h-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <defs>
            {COLORS.map((color, idx) => (
              <linearGradient key={idx} id={`gradient${idx}`} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.8} />
                <stop offset="100%" stopColor={color} stopOpacity={1} />
              </linearGradient>
            ))}
          </defs>

          <Pie
            data={formatted}
            dataKey="value"
            outerRadius="80%"
            innerRadius="50%"
            paddingAngle={2}
            stroke="#ffffff"
            strokeWidth={2}
            labelLine={false}
            label={CustomLabel}
          >
            {formatted.map((_, idx) => (
              <Cell
                key={idx}
                fill={`url(#gradient${idx % COLORS.length})`}
                style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </Pie>

          <Tooltip
            wrapperStyle={{
              borderRadius: 12,
              border: '1px solid rgba(229, 229, 229, 0.3)',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              backdropFilter: 'blur(20px)',
              padding: '12px 16px',
              fontFamily: "'Poppins', sans-serif"
            }}
            contentStyle={{
              border: 'none',
              borderRadius: 12,
              backgroundColor: 'transparent'
            }}
            formatter={(val, name) => [
              <span style={{ fontWeight: '600', color: '#171717', fontFamily: "'Poppins', sans-serif" }}>
                {val} {t('dashboard.interviews')}
              </span>,
              <span style={{ color: '#525252', fontSize: '12px', fontFamily: "'Poppins', sans-serif" }}>
                {name}
              </span>
            ]}
            labelStyle={{ color: '#262626', fontWeight: '600', fontFamily: "'Poppins', sans-serif" }}
          />
        </PieChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="absolute bottom-0 left-0 right-0 flex flex-wrap justify-center gap-2 px-2 pb-2">
        {formatted.slice(0, 4).map((item, idx) => (
          <div key={idx} className="flex items-center gap-1 text-xs">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: COLORS[idx % COLORS.length] }}
            />
            <span className="text-gray-600 truncate max-w-16" title={item.name}>
              {item.name.length > 8 ? `${item.name.slice(0, 8)}...` : item.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
